import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Check, Cloud, FileCheck2, Smartphone, WifiOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PublicLayout from '@/components/public/PublicLayout';
import MarketingShot from '@/components/public/MarketingShot';
import { FEATURES, featureBySlug, type Feature } from '@/content/features';
import { usePublicPlans, planFor } from '@/hooks/usePublicPlans';
import { planFromLabel } from '@/utils/publicPricing';
import { cn } from '@/utils/cn';

/** The features shown large on the home page, each beside its first screenshot. */
const SHOWCASE = ['spray', 'timesheets', 'irrigation', 'farm-jobs', 'nursery'];

const FREE_FOR_EVERYONE = [
  'Farms, blocks and plantings drawn on the map',
  'Clocking in and out, and requesting leave, for every staff member',
  'Inductions, policy acknowledgements and certifications',
  'Purchase orders raised for a farm and sent to the supplier',
  'The Android app, for everyone in your organisation',
];

function PriceTag({ feature }: { feature: Feature }) {
  const { plans } = usePublicPlans();
  const plan = planFor(plans, feature.planKey);
  if (!feature.planKey) {
    return <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Included</span>;
  }
  if (!plan) return null;
  return <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-700">{planFromLabel(plan)}</span>;
}

function Showcase({ feature, flip }: { feature: Feature; flip: boolean }) {
  const shot = feature.shots[0];
  return (
    <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
      <div className={cn('min-w-0 space-y-5', flip && 'lg:order-2')}>
        <div className="flex items-center gap-3">
          <span className={cn('flex h-10 w-10 items-center justify-center rounded-xl', feature.tone)}>
            <feature.icon className="h-5 w-5" />
          </span>
          <span className="text-sm font-semibold uppercase tracking-wide text-neutral-500">{feature.name}</span>
        </div>
        <h3 className="text-3xl font-bold tracking-tight text-neutral-900">{feature.headline}</h3>
        <p className="text-lg leading-relaxed text-neutral-600">{feature.intro}</p>
        <ul className="space-y-3">
          {feature.highlights.slice(0, 3).map((h) => (
            <li key={h.title} className="flex gap-3">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary-600" />
              <span className="text-neutral-700">
                <span className="font-semibold text-neutral-900">{h.title}.</span> {h.body}
              </span>
            </li>
          ))}
        </ul>
        <Link to={`/features/${feature.slug}`} className="inline-flex items-center gap-1 font-semibold text-primary-700 hover:text-primary-800">
          More about {feature.name.toLowerCase()} <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      {shot && <MarketingShot name={shot.shot} alt={shot.caption} className={cn(flip && 'lg:order-1')} />}
    </div>
  );
}

function Landing() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const showcase = SHOWCASE.map(featureBySlug).filter((f): f is Feature => !!f);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary-50 via-white to-white">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 lg:px-8 lg:pt-20">
          <div className="mx-auto max-w-3xl space-y-6 text-center">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">
              For orchards, vineyards and nurseries
            </p>
            <h1 className="text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl lg:text-6xl">
              Run the whole farm from one account
            </h1>
            <p className="text-lg leading-8 text-neutral-600 sm:text-xl">
              Spray records, timesheets and payroll, irrigation, jobs and harvest, equipment, nursery orders and
              accounts. Every record knows which block it belongs to, and your staff use it from their phone.
            </p>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/register" className="w-full rounded-xl bg-primary-600 px-6 py-3 text-base font-semibold text-white shadow-soft hover:bg-primary-700 sm:w-auto">
                Get started free
              </Link>
              <Link to="/pricing" className="w-full rounded-xl border border-neutral-300 bg-white px-6 py-3 text-base font-semibold text-neutral-800 hover:bg-neutral-50 sm:w-auto">
                See pricing
              </Link>
            </div>
            <p className="text-sm text-neutral-500">Free to sign up. Pay only for the modules you switch on.</p>
          </div>
          <MarketingShot name="dashboard" alt="The FactreeTech dashboard" eager className="mx-auto mt-14 max-w-5xl" />
        </div>
      </section>

      {/* Module grid */}
      <section id="features" className="scroll-mt-20 bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl space-y-4 text-center">
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">Switch on what your farm needs</h2>
            <p className="text-lg text-neutral-600">
              Each part of FactreeTech is a module. Start with one and add the rest when you are ready: they share the
              same farms, people and equipment.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <Link
                key={f.slug}
                to={`/features/${f.slug}`}
                className="group flex min-w-0 flex-col rounded-2xl border border-neutral-200 bg-white p-6 transition hover:border-primary-300 hover:shadow-medium"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className={cn('flex h-11 w-11 items-center justify-center rounded-xl', f.tone)}>
                    <f.icon className="h-6 w-6" />
                  </span>
                  <PriceTag feature={f} />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-neutral-900 group-hover:text-primary-700">{f.name}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-600">{f.summary}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-700">
                  Learn more <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase */}
      <section className="bg-neutral-50 py-20">
        <div className="mx-auto max-w-7xl space-y-24 px-4 sm:px-6 lg:px-8">
          {showcase.map((f, i) => (
            <Showcase key={f.slug} feature={f} flip={i % 2 === 1} />
          ))}
        </div>
      </section>

      {/* Phone, compliance, accounting */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="mx-auto max-w-2xl text-center text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            Built for the paddock, the shed and the office
          </h2>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Smartphone, title: 'On every phone', body: 'Staff clock in, run pre-starts, record sprays, start bins and read their inductions in the app or the browser.' },
              { icon: WifiOff, title: 'Dead spots are fine', body: 'Crew mode, bins and stocktake counts are stored on the phone first and sent when signal comes back.' },
              { icon: FileCheck2, title: 'Audit ready', body: 'Spray records, calibrations, credentials and training come together in a season pack for Freshcare or GLOBALG.A.P. audits.' },
              { icon: Cloud, title: 'Xero and MYOB', body: 'Approved timesheets and leave go to Xero Payroll or MYOB, and invoices and purchase orders to Xero. No retyping.' },
            ].map((item) => (
              <div key={item.title} className="rounded-2xl border border-neutral-200 p-6">
                <item.icon className="h-7 w-7 text-primary-600" />
                <h3 className="mt-4 font-semibold text-neutral-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Free */}
      <section className="bg-neutral-50 py-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div className="space-y-4">
            <h2 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">Free with every account</h2>
            <p className="text-lg text-neutral-600">
              Some things every farm needs, whatever else it uses. They cost nothing, for everyone in your organisation.
            </p>
            <Link to="/pricing" className="inline-flex items-center gap-1 font-semibold text-primary-700 hover:text-primary-800">
              See what the modules cost <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <ul className="space-y-3 rounded-2xl border border-neutral-200 bg-white p-6">
            {FREE_FOR_EVERYONE.map((line) => (
              <li key={line} className="flex gap-3 text-neutral-700">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                {line}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-gradient-primary py-20">
        <div className="absolute inset-0 bg-primary-900/20"></div>
        <div className="relative mx-auto max-w-3xl space-y-6 px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">Ready to get started?</h2>
          <p className="text-lg text-primary-100">
            Create your account, draw your first farm, and invite your team. Switch on modules when you need them.
          </p>
          <div className="flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/register" className="w-full rounded-xl bg-white px-6 py-3 font-semibold text-primary-700 hover:bg-primary-50 sm:w-auto">
              Create your account
            </Link>
            <Link to="/login" className="w-full rounded-xl border border-white px-6 py-3 font-semibold text-white hover:bg-white/10 sm:w-auto">
              Sign in
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

export default Landing;
